import { useLayoutEffect, type RefObject } from 'react'
import { animate, createTimeline, set } from 'animejs'
import { FLAVOUR_TRANSITION_TUNING } from '../config/flavourTuning'
import {
  FLAVOUR_IDS,
  FLAVOUR_PHASE_EVENT,
  getFlavourIndex,
  isFlavourId,
  type FlavourPhase,
  type FlavourPhaseDetail,
  type FlavourPhaseSource,
} from '../utils/flavourPhase'

const WHEEL_TRIGGER_DISTANCE = 54
const WHEEL_IDLE_MS = 170
const TOUCH_TRIGGER_DISTANCE = 52
const DECK_SCROLL_DURATION_MS = 860
const PHASE_GUARD_TAIL_MS = 520
const GESTURE_LOCK_TAIL_MS = 90
const UI_COPY_TRANSITION_MS = 520

export function useFlavourDeck(
  root: RefObject<HTMLDivElement | null>,
  motionEnabled: boolean,
) {
  useLayoutEffect(() => {
    const rootElement = root.current
    const journeyRoot = rootElement?.querySelector<HTMLElement>(
      '[data-can-journey]',
    )
    const section = journeyRoot?.querySelector<HTMLElement>('#flavour')
    if (!rootElement || !journeyRoot || !section || !motionEnabled) return

    const steps = Array.from(
      section.querySelectorAll<HTMLElement>('[data-flavour-step]'),
    )
    const intro = section.querySelector<HTMLElement>('.flavour-voyage__intro')
    const outro = section.querySelector<HTMLElement>('.flavour-voyage__exit')
    const nextSection = journeyRoot.nextElementSibling as HTMLElement | null
    const chargeSegments = Array.from(
      section.querySelectorAll<HTMLElement>('.flavour-voyage__charge i'),
    )
    const chargeBar = section.querySelector<HTMLElement>(
      '.flavour-voyage__charge',
    )
    const mobileViewport = window.matchMedia('(max-width: 759px)')
    const ambients = [
      journeyRoot.querySelector<HTMLElement>('.can-journey__ambient--deep'),
      journeyRoot.querySelector<HTMLElement>('.can-journey__ambient--ancient'),
      journeyRoot.querySelector<HTMLElement>('.can-journey__ambient--event'),
    ]
    const entries = steps.map((step, index) => ({
      step,
      id: FLAVOUR_IDS[index],
      copy: step.querySelector<HTMLElement>('.flavour-step__copy'),
    }))

    if (entries.length !== FLAVOUR_IDS.length) return

    let currentPhase: FlavourPhase | null = null
    let phaseTimeline: ReturnType<typeof createTimeline> | undefined
    let chargeTimeline: ReturnType<typeof createTimeline> | undefined
    let chargePulseTimeline: ReturnType<typeof createTimeline> | undefined
    let scrollAnimation: ReturnType<typeof animate> | undefined
    let scrollFrame = 0
    let pendingPhase: FlavourPhase | null = null
    let pendingPhaseUntil = 0
    let anchorNavigationActive = false
    let wheelAccumulator = 0
    let wheelDirection = 0
    let wheelResetTimer = 0
    let gestureUnlockTimer = 0
    let gestureLockedUntil = 0
    let lastGestureInput = 0
    let gestureLocked = false
    let touchStartX: number | null = null
    let touchStartY: number | null = null
    let forwardCharge = 0
    let chargeIdleTimer = 0
    let chargeDecayTimer = 0
    let chargeResetTimer = 0
    let chargeDecayGeneration = 0
    const hashFlavour = window.location.hash.replace('#flavour-', '')
    let introGateComplete = isFlavourId(hashFlavour)

    const ambientTargetsFor = (phase: FlavourPhase) => {
      if (phase === 'ancient-spark') return [0.2, 1, 0]
      if (phase === 'event-horizon') return [0.2, 0, 1]
      if (phase === 'outro' || phase === 'after') return [0.2, 0, 0]
      return [1, 0, 0]
    }

    const requiredForwardInputs = () =>
      mobileViewport.matches
        ? FLAVOUR_TRANSITION_TUNING.mobileForwardInputs
        : FLAVOUR_TRANSITION_TUNING.desktopForwardInputs

    const chargeFillFor = (count: number, segmentIndex: number) => {
      const progress = count / requiredForwardInputs()
      return Math.min(Math.max(progress * chargeSegments.length - segmentIndex, 0), 1)
    }

    const animateChargeVisual = (count: number, duration = 240) => {
      chargeTimeline?.cancel()
      chargeTimeline = createTimeline()
      chargeSegments.forEach((segment, index) => {
        chargeTimeline?.add(
          segment,
          {
            scaleX: chargeFillFor(count, index),
            duration,
            ease: 'inOutSine',
          },
          0,
        )
      })
    }

    const pulseChargeCue = () => {
      if (!chargeBar) return
      chargePulseTimeline?.cancel()
      chargePulseTimeline = createTimeline()
        .add(
          chargeBar,
          {
            y: [0, -3, 0],
            scaleY: [1, 1.42, 1],
            duration: 520,
            ease: 'outExpo',
          },
          0,
        )
        .add(
          chargeBar.children,
          {
            opacity: [0.72, 1, 0.82],
            duration: 560,
            ease: 'outQuart',
          },
          0,
        )
    }

    const cancelChargeDecay = () => {
      chargeDecayGeneration += 1
      window.clearTimeout(chargeIdleTimer)
      window.clearTimeout(chargeDecayTimer)
    }

    const decayCharge = (generation: number) => {
      if (generation !== chargeDecayGeneration || forwardCharge <= 0) return
      forwardCharge -= 1
      const duration = mobileViewport.matches ? 480 : 230
      animateChargeVisual(forwardCharge, duration)
      if (forwardCharge > 0) {
        chargeDecayTimer = window.setTimeout(
          () => decayCharge(generation),
          duration + 80,
        )
      }
    }

    const scheduleChargeDecay = () => {
      cancelChargeDecay()
      const generation = chargeDecayGeneration
      chargeIdleTimer = window.setTimeout(
        () => decayCharge(generation),
        FLAVOUR_TRANSITION_TUNING.chargeIdleMs,
      )
    }

    const clearForwardCharge = (duration = 260) => {
      cancelChargeDecay()
      forwardCharge = 0
      animateChargeVisual(0, duration)
    }

    const applyPhaseImmediately = (phase: FlavourPhase) => {
      const activeIndex = getFlavourIndex(phase)
      entries.forEach(({ copy }, index) => {
        if (copy) {
          set(copy, {
            y:
              activeIndex < 0
                ? '0.45rem'
                : index < activeIndex
                  ? '-0.45rem'
                  : index > activeIndex
                    ? '0.45rem'
                    : '0rem',
          })
        }
      })
      const ambientTargets = ambientTargetsFor(phase)
      ambients.forEach((ambient, index) => {
        if (ambient) set(ambient, { opacity: ambientTargets[index] })
      })
      chargeSegments.forEach((segment, index) => {
        set(segment, { scaleX: chargeFillFor(forwardCharge, index) })
      })
    }

    const animateToPhase = (
      phase: FlavourPhase,
      previousPhase: FlavourPhase | null,
    ) => {
      const activeIndex = getFlavourIndex(phase)
      const ambientTargets = ambientTargetsFor(phase)
      const isOriginHandoff =
        (previousPhase === 'before' && phase === 'intro') ||
        (previousPhase === 'intro' && phase === 'before')
      const ambientDuration = isOriginHandoff
        ? FLAVOUR_TRANSITION_TUNING.originHandoffDurationMs
        : FLAVOUR_TRANSITION_TUNING.backgroundTransitionDurationMs
      phaseTimeline?.cancel()
      phaseTimeline = createTimeline()

      entries.forEach(({ copy }, index) => {
        if (copy) {
          phaseTimeline?.add(
            copy,
            {
              y:
                activeIndex < 0
                  ? '0.45rem'
                  : index < activeIndex
                    ? '-0.45rem'
                    : index > activeIndex
                      ? '0.45rem'
                      : '0rem',
              duration:
                UI_COPY_TRANSITION_MS,
              ease: 'outQuint',
            },
            0,
          )
        }
      })

      ambients.forEach((ambient, index) => {
        if (!ambient) return
        phaseTimeline?.add(
          ambient,
          {
            opacity: ambientTargets[index],
            duration: ambientDuration,
            ease: 'inOutSine',
          },
          0,
        )
      })
    }

    const setPhase = (
      phase: FlavourPhase,
      source: FlavourPhaseSource,
      immediate = false,
    ) => {
      if (phase === currentPhase) {
        syncDirectionControls()
        return
      }
      const previousPhase = currentPhase
      currentPhase = phase
      if (previousPhase && source !== 'gesture') clearForwardCharge()
      journeyRoot.dataset.flavourPhase = phase
      journeyRoot.dataset.flavourDeck =
        phase === 'intro' || phase === 'outro' || isFlavourId(phase)
          ? 'active'
          : 'inactive'
      syncDirectionControls()

      if (immediate) applyPhaseImmediately(phase)
      else animateToPhase(phase, previousPhase)

      const detail: FlavourPhaseDetail = {
        phase,
        previousPhase,
        source,
      }
      journeyRoot.dispatchEvent(
        new CustomEvent<FlavourPhaseDetail>(FLAVOUR_PHASE_EVENT, {
          bubbles: true,
          detail,
        }),
      )
    }

    const resolvePhaseAtViewportCentre = (): FlavourPhase => {
      const focusY = window.innerHeight * 0.5
      const sectionBounds = section.getBoundingClientRect()
      if (sectionBounds.top > focusY) return 'before'
      if (sectionBounds.bottom <= focusY) return 'after'

      const introBounds = intro?.getBoundingClientRect()
      const introHasReachedActivation =
        introBounds &&
        introBounds.bottom <=
          window.innerHeight *
            FLAVOUR_TRANSITION_TUNING.introActivationViewportRatio
      const introGateIsHolding =
        !introGateComplete &&
        (currentPhase === null ||
          currentPhase === 'before' ||
          currentPhase === 'intro')

      // Geometry alone must not promote the first flavour while entering the
      // deck. A high-resolution wheel/trackpad event can move the viewport
      // beyond the activation threshold before the next animation frame; the
      // logical gate keeps that overshoot in the intro until it is charged.
      if (introGateIsHolding && introHasReachedActivation) return 'intro'

      for (const { step, id } of entries) {
        const bounds = step.getBoundingClientRect()
        if (bounds.top <= focusY && bounds.bottom > focusY) return id
      }

      const firstBounds = entries[0].step.getBoundingClientRect()
      if (firstBounds.top > focusY) {
        return introHasReachedActivation ? 'intro' : 'before'
      }
      return 'outro'
    }

    const checkPhaseFromScroll = () => {
      scrollFrame = 0
      // A deliberate navigation must be allowed to cross the deck in either
      // direction without its intermediate geometry recapturing the intro.
      if (anchorNavigationActive) return
      const phase = resolvePhaseAtViewportCentre()
      if (pendingPhase) {
        if (phase === pendingPhase) {
          pendingPhase = null
        } else if (performance.now() < pendingPhaseUntil) {
          return
        } else {
          pendingPhase = null
        }
      }
      if (
        phase === 'intro' &&
        currentPhase !== 'intro' &&
        !introGateComplete
      ) {
        beginGestureTransition('intro', 'scroll')
        return
      }
      setPhase(phase, 'scroll')
    }

    const handleScroll = () => {
      if (scrollFrame) return
      scrollFrame = window.requestAnimationFrame(checkPhaseFromScroll)
    }

    const animateScrollTo = (target: number, onComplete?: () => void) => {
      scrollAnimation?.cancel()
      const scrollState = { y: window.scrollY }
      const maxScroll = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        0,
      )
      scrollAnimation = animate(scrollState, {
        y: Math.min(Math.max(target, 0), maxScroll),
        duration: DECK_SCROLL_DURATION_MS,
        ease: 'inOutQuart',
        onUpdate: () => window.scrollTo({ top: scrollState.y, behavior: 'instant' }),
        onComplete: () => onComplete?.(),
      })
    }

    const scrollElementToViewportCentre = (element: HTMLElement) => {
      const bounds = element.getBoundingClientRect()
      const target =
        window.scrollY + bounds.top + bounds.height * 0.5 - window.innerHeight * 0.5
      animateScrollTo(target)
    }

    const scrollToPhase = (phase: FlavourPhase) => {
      if (isFlavourId(phase)) {
        const index = FLAVOUR_IDS.indexOf(phase)
        scrollElementToViewportCentre(entries[index].step)
        history.replaceState(null, '', `#flavour-${phase}`)
        return
      }
      if (phase === 'intro' && intro) {
        const bounds = intro.getBoundingClientRect()
        animateScrollTo(
          window.scrollY +
            bounds.bottom -
            window.innerHeight *
              FLAVOUR_TRANSITION_TUNING.introAnchorViewportRatio,
        )
        history.replaceState(null, '', '#flavour')
        return
      }
      if (phase === 'outro') {
        if (nextSection) {
          const bounds = nextSection.getBoundingClientRect()
          animateScrollTo(window.scrollY + bounds.top)
        } else if (outro) {
          scrollElementToViewportCentre(outro)
        }
      }
    }

    const scheduleGestureUnlock = () => {
      window.clearTimeout(gestureUnlockTimer)
      const unlock = () => {
        const now = performance.now()
        const remainingLock = gestureLockedUntil - now
        const remainingIdle = WHEEL_IDLE_MS - (now - lastGestureInput)
        const remaining = Math.max(remainingLock, remainingIdle)
        if (remaining > 0) {
          gestureUnlockTimer = window.setTimeout(unlock, remaining + 8)
          return
        }
        gestureLocked = false
        wheelAccumulator = 0
        wheelDirection = 0
        syncDirectionControls()
      }
      gestureUnlockTimer = window.setTimeout(unlock, WHEEL_IDLE_MS)
    }

    function beginGestureTransition(
      phase: FlavourPhase,
      source: FlavourPhaseSource = 'gesture',
    ) {
      cancelChargeDecay()
      window.clearTimeout(chargeResetTimer)
      const previousPhase = currentPhase
      if (phase === 'intro') introGateComplete = false
      if (phase === FLAVOUR_IDS[0] && previousPhase === 'intro') {
        introGateComplete = true
      }
      pendingPhase = phase === 'outro' ? 'after' : phase
      pendingPhaseUntil =
        performance.now() +
        DECK_SCROLL_DURATION_MS +
        PHASE_GUARD_TAIL_MS
      gestureLocked = true
      gestureLockedUntil =
        performance.now() + DECK_SCROLL_DURATION_MS + GESTURE_LOCK_TAIL_MS
      lastGestureInput = performance.now()
      wheelAccumulator = 0
      wheelDirection = 0
      setPhase(phase, source)
      scrollToPhase(phase)
      scheduleGestureUnlock()
      chargeResetTimer = window.setTimeout(() => {
        forwardCharge = 0
        animateChargeVisual(0, 420)
      }, 360)
    }

    const targetForDirection = (
      phase: FlavourPhase,
      direction: -1 | 1,
    ): FlavourPhase | null => {
      if (phase === 'intro') return direction > 0 ? FLAVOUR_IDS[0] : null
      if (phase === 'outro') return direction < 0 ? FLAVOUR_IDS[2] : null
      const index = getFlavourIndex(phase)
      if (index < 0) return null
      if (direction < 0) return index === 0 ? 'intro' : FLAVOUR_IDS[index - 1]
      return index === FLAVOUR_IDS.length - 1
        ? 'outro'
        : FLAVOUR_IDS[index + 1]
    }

    function syncDirectionControls() {
      section?.querySelectorAll<HTMLElement>('[data-deck-direction]').forEach((control) => {
        const direction = control.dataset.deckDirection === 'previous' ? -1 : 1
        const disabled = gestureLocked || anchorNavigationActive || !currentPhase ||
          targetForDirection(currentPhase, direction) === null
        // Keep the focused control in place while its transition runs. Native
        // disabled buttons can drop keyboard focus as soon as they are clicked.
        control.setAttribute('aria-disabled', String(disabled))
        control.dataset.disabled = String(disabled)
      })
    }

    const handleRootClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey ||
        !(event.target instanceof Element)
      ) return

      const directionControl = event.target.closest<HTMLElement>('[data-deck-direction]')
      if (directionControl && section.contains(directionControl)) {
        event.preventDefault()
        if (gestureLocked || anchorNavigationActive || !currentPhase) return
        const direction = directionControl.dataset.deckDirection === 'previous' ? -1 : 1
        const target = targetForDirection(currentPhase, direction)
        if (!target) return
        clearForwardCharge(180)
        pulseChargeCue()
        beginGestureTransition(target)
        return
      }

      const link = event.target.closest<HTMLAnchorElement>('a[href]')
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return
      const url = new URL(link.href, window.location.href)
      if (
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname ||
        url.search !== window.location.search || !url.hash
      ) return

      let targetId: string
      try {
        targetId = decodeURIComponent(url.hash.slice(1))
      } catch {
        return
      }
      const targetElement = document.getElementById(targetId)
      if (!targetElement || !rootElement.contains(targetElement)) return

      const targetEntry = entries.find(({ step }) => step.contains(targetElement))
      const isIntroTarget = targetElement === section || Boolean(intro?.contains(targetElement))
      const keyboardNavigation = event.detail === 0
      const focusDestination = targetEntry?.copy?.querySelector<HTMLElement>('h3') ?? targetElement
      const bounds = targetElement.getBoundingClientRect()
      const pageTop = window.scrollY + bounds.top
      const sectionTop = window.scrollY + section.getBoundingClientRect().top
      let phase: FlavourPhase = pageTop < sectionTop ? 'before' : 'after'
      const scrollPadding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0
      const scrollMargin = parseFloat(getComputedStyle(targetElement).scrollMarginTop) || 0
      let targetY = pageTop - scrollPadding - scrollMargin
      if (targetEntry) {
        phase = targetEntry.id
        const stepBounds = targetEntry.step.getBoundingClientRect()
        targetY = window.scrollY + stepBounds.top + stepBounds.height * 0.5 - window.innerHeight * 0.5
      } else if (isIntroTarget && intro) {
        phase = 'intro'
        targetY = window.scrollY + intro.getBoundingClientRect().bottom -
          window.innerHeight * FLAVOUR_TRANSITION_TUNING.introAnchorViewportRatio
      }

      event.preventDefault()
      cancelChargeDecay()
      window.clearTimeout(chargeResetTimer)
      clearForwardCharge(180)
      anchorNavigationActive = true
      introGateComplete = isFlavourId(phase) || phase === 'after'
      pendingPhase = phase
      pendingPhaseUntil = performance.now() + DECK_SCROLL_DURATION_MS + PHASE_GUARD_TAIL_MS
      gestureLocked = true
      gestureLockedUntil = performance.now() + DECK_SCROLL_DURATION_MS + GESTURE_LOCK_TAIL_MS
      lastGestureInput = performance.now()
      wheelAccumulator = 0
      wheelDirection = 0
      touchStartX = null
      touchStartY = null
      setPhase(phase, 'scroll')
      if (window.location.hash !== url.hash) history.pushState(history.state, '', url.hash)
      animateScrollTo(targetY, () => {
        anchorNavigationActive = false
        pendingPhaseUntil = performance.now() + PHASE_GUARD_TAIL_MS
        if (keyboardNavigation) {
          if (!focusDestination.hasAttribute('tabindex')) focusDestination.tabIndex = -1
          focusDestination.focus({ preventScroll: true })
        }
        syncDirectionControls()
        handleScroll()
      })
      scheduleGestureUnlock()
    }

    const registerDirectionalInput = (
      direction: -1 | 1,
      target: FlavourPhase,
    ) => {
      pulseChargeCue()
      if (direction < 0) {
        clearForwardCharge(180)
        beginGestureTransition(target)
        return
      }

      cancelChargeDecay()
      forwardCharge = Math.min(
        requiredForwardInputs(),
        forwardCharge + 1,
      )
      animateChargeVisual(
        forwardCharge,
        mobileViewport.matches ? 320 : 150,
      )
      if (forwardCharge >= requiredForwardInputs()) {
        beginGestureTransition(target)
      } else {
        scheduleChargeDecay()
      }
    }

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
      if (anchorNavigationActive) {
        event.preventDefault()
        return
      }
      if (!currentPhase) return
      const direction = Math.sign(event.deltaY) as -1 | 0 | 1
      if (!direction) return
      const unit =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? window.innerHeight
            : 1

      if (currentPhase === 'before' && direction > 0 && intro) {
        const projectedIntroBottom =
          intro.getBoundingClientRect().bottom - event.deltaY * unit
        const activationLine =
          window.innerHeight *
          FLAVOUR_TRANSITION_TUNING.introActivationViewportRatio
        if (projectedIntroBottom <= activationLine) {
          event.preventDefault()
          beginGestureTransition('intro')
          return
        }
      }

      if (
        gestureLocked &&
        (currentPhase === 'intro' ||
          currentPhase === 'outro' ||
          isFlavourId(currentPhase))
      ) {
        event.preventDefault()
        lastGestureInput = performance.now()
        scheduleGestureUnlock()
        return
      }
      const target = targetForDirection(currentPhase, direction)
      if (!target) return

      event.preventDefault()
      lastGestureInput = performance.now()
      if (direction !== wheelDirection) {
        wheelAccumulator = 0
        wheelDirection = direction
      }
      wheelAccumulator += event.deltaY * unit
      window.clearTimeout(wheelResetTimer)
      wheelResetTimer = window.setTimeout(() => {
        wheelAccumulator = 0
        wheelDirection = 0
      }, WHEEL_IDLE_MS)

      if (Math.abs(wheelAccumulator) >= WHEEL_TRIGGER_DISTANCE) {
        wheelAccumulator = 0
        registerDirectionalInput(direction, target)
      }
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (anchorNavigationActive) return
      if (event.touches.length !== 1 || !currentPhase) return
      touchStartX = event.touches[0].clientX
      touchStartY = event.touches[0].clientY
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (anchorNavigationActive) {
        if (event.touches.length === 1) event.preventDefault()
        return
      }
      if (
        touchStartX === null ||
        touchStartY === null ||
        !currentPhase ||
        event.touches.length !== 1
      ) {
        return
      }
      const deltaX = event.touches[0].clientX - touchStartX
      const deltaY = event.touches[0].clientY - touchStartY
      if (Math.abs(deltaY) <= Math.abs(deltaX)) return
      const direction = deltaY < 0 ? 1 : -1
      if (targetForDirection(currentPhase, direction)) event.preventDefault()
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (
        touchStartX === null ||
        touchStartY === null ||
        !currentPhase ||
        event.changedTouches.length === 0
      ) {
        touchStartX = null
        touchStartY = null
        return
      }
      const deltaX = event.changedTouches[0].clientX - touchStartX
      const deltaY = event.changedTouches[0].clientY - touchStartY
      touchStartX = null
      touchStartY = null
      if (
        gestureLocked ||
        Math.abs(deltaY) < TOUCH_TRIGGER_DISTANCE ||
        Math.abs(deltaY) <= Math.abs(deltaX) * 1.15
      ) {
        return
      }
      const direction = deltaY < 0 ? 1 : -1
      const target = targetForDirection(currentPhase, direction)
      if (target) registerDirectionalInput(direction, target)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const targetElement = event.target as HTMLElement | null
      if (
        event.repeat || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey ||
        targetElement?.closest(
          'input, textarea, select, button, a, [role="button"], [contenteditable="true"]',
        ) ||
        !currentPhase
      ) {
        return
      }
      const forward =
        event.key === 'ArrowDown' ||
        event.key === 'PageDown' ||
        (event.key === ' ' && !event.shiftKey)
      const backward =
        event.key === 'ArrowUp' ||
        event.key === 'PageUp' ||
        (event.key === ' ' && event.shiftKey)
      if (!forward && !backward) return
      if (anchorNavigationActive) {
        event.preventDefault()
        return
      }
      const direction = forward ? 1 : -1
      const target = targetForDirection(currentPhase, direction)
      if (!target) return
      event.preventDefault()
      if (!gestureLocked) registerDirectionalInput(direction, target)
    }

    const initialPhase = resolvePhaseAtViewportCentre()
    if (initialPhase === 'intro' && !introGateComplete) {
      beginGestureTransition('intro', 'initial')
    } else {
      setPhase(initialPhase, 'initial', true)
    }
    const handleViewportModeChange = () => clearForwardCharge(0)
    mobileViewport.addEventListener('change', handleViewportModeChange)
    rootElement.addEventListener('click', handleRootClick)
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      phaseTimeline?.cancel()
      chargeTimeline?.cancel()
      chargePulseTimeline?.cancel()
      scrollAnimation?.cancel()
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame)
      window.clearTimeout(wheelResetTimer)
      window.clearTimeout(gestureUnlockTimer)
      window.clearTimeout(chargeIdleTimer)
      window.clearTimeout(chargeDecayTimer)
      window.clearTimeout(chargeResetTimer)
      mobileViewport.removeEventListener('change', handleViewportModeChange)
      rootElement.removeEventListener('click', handleRootClick)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('keydown', handleKeyDown)
      delete journeyRoot.dataset.flavourDeck
      delete journeyRoot.dataset.flavourPhase
    }
  }, [motionEnabled, root])
}
